import React from "react";
import { Box, Typography } from "@mui/material";

import { BriefView } from "../../../../components/Brief/BriefView";
import { BRAND_PROJECT_STATUSES } from "../../../videoProduction/helper";
import { PublicProject } from "../OrderPublicPage";

interface Props {
    project: PublicProject;
}

export function getStatusMessage(status: string): { title: string; body: string } {
    switch (status) {
        case BRAND_PROJECT_STATUSES.PENDING:
            return {
                title: "Pending Approval",
                body: "Your project is pending approval by our team. You'll be notified once it has been reviewed."
            };

        case BRAND_PROJECT_STATUSES.BRIEF_REVIEW_NEEDED:
            return {
                title: "Brief Under Review",
                body: "Your brief has been submitted and is currently being reviewed by our team. You'll be notified once it has been approved."
            };

        case BRAND_PROJECT_STATUSES.BRIEF_DENIED:
            return {
                title: "Brief Denied",
                body: "Your brief has been denied. Please review the feedback below and resubmit your updated brief."
            };

        case BRAND_PROJECT_STATUSES.EXPERT_PROPOSALS_NEEDED:
            return {
                title: "Expert Proposals in Progress",
                body: "We are in the process of proposing experts for your project. You'll be notified when experts are ready for your selection."
            };

        case BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED:
        case BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_DENIED:
            return {
                title: "Expert Proposal Review in Progress",
                body: "We are in the process of reviewing the proposed experts for your project. You'll be notified when experts are ready for your selection."
            };

        case BRAND_PROJECT_STATUSES.SCHEDULING:
            return {
                title: "Scheduling",
                body: "Your project is being scheduled. You'll be notified once filming has been arranged."
            };

        case BRAND_PROJECT_STATUSES.PRE_PRODUCTION:
            return {
                title: "Pre-Production - Shoot Date Scheduled",
                body: "Your project shoot has been scheduled. We'll notify you when filming is complete."
            };

        case BRAND_PROJECT_STATUSES.FILMING:
            return {
                title: "Filming",
                body: "Your project is currently being filmed. You'll be notified when editing begins."
            };

        case BRAND_PROJECT_STATUSES.EDITING:
            return {
                title: "In Post-Production",
                body: "Your project is currently in post-production. You'll be notified when your video is ready for review."
            };

        case BRAND_PROJECT_STATUSES.FINAL_DELIVERED:
            return {
                title: "Final Delivered",
                body: "Your project has been completed. Thank you for working with us! Your final videos are available to download above."
            };

        case BRAND_PROJECT_STATUSES.CANCELLED:
            return {
                title: "Project Cancelled",
                body: "This project has been cancelled. Please contact our support team if you have any questions."
            };

        default:
            return {
                title: "In Progress",
                body: "Your project is currently being processed. You'll be notified when action is required."
            };
    }
}

export function PublicProjectStatusSection({ project }: Props) {
    const { title, body } = getStatusMessage(project.status);

    const briefForView = project.brief
        ? {
              id: project.brief.id,
              created_at: "",
              updated_at: "",
              ...(project.brief.intakeForm || {}),
              ...(project.brief.questions || {})
          }
        : null;

    return (
        <Box className="flex flex-col gap-6">
            {/* Status callout */}
            <Box
                className="flex flex-col gap-1 p-4 rounded-xl border border-border-token"
                sx={{ backgroundColor: "var(--surface-2)" }}
            >
                <Typography variant="body2" fontWeight={600} color="textPrimary">
                    {title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {body}
                </Typography>
            </Box>

            {/* Brief (read-only) — skip for Brief Review since it's just a status-only view */}
            {briefForView && project.status !== BRAND_PROJECT_STATUSES.BRIEF_REVIEW_NEEDED && (
                <BriefView brief={briefForView as any} projectTitle={project.title} />
            )}
        </Box>
    );
}
